import type { ProblemDetails } from '@construtora/contracts';

/**
 * Cliente HTTP.
 *
 * Nenhum `fetch` dentro de componente: toda chamada passa por um hook em
 * `features/*\/api`, que por sua vez usa este cliente (.spec/002, seção 7).
 */

// `ImportMetaEnv` declara um índice `any`, então a leitura precisa ser
// estreitada aqui: sem isso o tipo `any` se espalha por toda chamada de API.
const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:3000';

/**
 * Planos de acesso.
 *
 * O sistema tem dois: o do usuário de conta e o do console de operação. Cada
 * um tem token próprio, e o plano de cada requisição é decidido pelo
 * **caminho**, não por parâmetro no ponto de chamada — um argumento a mais
 * seria esquecido em algum lugar, e o sintoma seria o token do cliente indo
 * para uma rota de operação.
 */
export type Plano = 'TENANT' | 'OPERADOR';

const PREFIXO_OPERACAO = '/api/v1/operacao';

export function planoDoCaminho(caminho: string): Plano {
  return caminho.startsWith(PREFIXO_OPERACAO) ? 'OPERADOR' : 'TENANT';
}

/** Access tokens vivem **em memória**, nunca em localStorage (.spec/005). */
const tokens: Record<Plano, string | null> = { TENANT: null, OPERADOR: null };

/**
 * A aba desistiu de renovar, porque o cookie é de outra conta.
 *
 * Trava por plano, e existe para **não insistir**. Sem ela, cada 401 seguinte
 * dispararia outra renovação pelo mesmo cookie compartilhado — e cada renovação
 * rotaciona o refresh token da conta que é dona dele. A aba abandonada ficaria
 * queimando a sessão legítima da outra aba a cada requisição, até o backend ler
 * a sequência como reuso e derrubar as duas. O defeito de mostrar a conta errada
 * viraria o defeito de derrubar quem não fez nada.
 *
 * Destrava sozinha quando um token novo entra em memória, que é o que acontece
 * ao entrar de novo — ver `definirAccessToken`.
 */
const renovacaoBloqueada: Record<Plano, boolean> = { TENANT: false, OPERADOR: false };

export function definirAccessToken(token: string | null, plano: Plano = 'TENANT'): void {
  tokens[plano] = token;

  // Guardar um token é o sinal de que uma identidade foi estabelecida agora —
  // login ou restauração de sessão. Só isso destrava; encerrar (token `null`)
  // não destrava, senão a trava não sobreviveria ao próprio encerramento.
  if (token !== null) renovacaoBloqueada[plano] = false;
}

export function temAccessToken(plano: Plano = 'TENANT'): boolean {
  return tokens[plano] !== null;
}

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly problema: ProblemDetails,
  ) {
    super(problema.detail);
    this.name = 'ErroApi';
  }

  get code(): string {
    return this.problema.title;
  }

  /** Código exibido na tela de erro; é o que o usuário informa ao suporte. */
  get requestId(): string | undefined {
    return this.problema.requestId;
  }

  temCodigo(code: string): boolean {
    return (
      this.problema.title === code || this.problema.errors?.some((e) => e.code === code) === true
    );
  }

  /** Erros por campo, no formato que o React Hook Form usa em `setError`. */
  camposComErro(): { campo: string; mensagem: string }[] {
    return (this.problema.errors ?? [])
      .filter((e) => e.path.length > 0)
      .map((e) => ({ campo: e.path, mensagem: e.mensagem ?? this.problema.detail }));
  }
}

/**
 * Traduz qualquer erro em texto para o usuário.
 *
 * O `requestId` sai junto porque é o que o usuário informa ao suporte: sem o
 * código na tela, a reclamação chega como "deu erro ontem à tarde" e ninguém
 * localiza a requisição no log (.spec/008, seção 9).
 */
export function descreverErro(erro: unknown): { texto: string; requestId?: string } {
  if (erro instanceof ErroApi) {
    return erro.requestId
      ? { texto: erro.message, requestId: erro.requestId }
      : { texto: erro.message };
  }

  if (erro instanceof Error) return { texto: erro.message };

  // Falha de rede chega como `TypeError` sem mensagem útil, e "Failed to fetch"
  // não diz nada a quem está usando o sistema.
  return { texto: 'Não foi possível concluir a operação. Verifique sua conexão e tente de novo.' };
}

export interface OpcoesRequisicao extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Escrita idempotente: permite reenviar sem duplicar (.spec/001, seção 3). */
  idempotencyKey?: string;
  /**
   * `crua` devolve a `Response`, sem passar pelo `json()`.
   *
   * Existe para a rota que entrega **arquivo** — a exportação assinada do log
   * (`.spec/017`, seção 13). Sem isto, `interpretar` chamaria `json()` sobre um
   * CSV, o `catch` devolveria `null`, e a tela receberia sucesso com conteúdo
   * vazio: o arquivo sumiria sem erro nenhum aparecer.
   *
   * **Só o caminho de sucesso muda.** O erro continua sendo lido como JSON e
   * vira `ErroApi` como em qualquer outra rota — a API devolve *problem details*
   * mesmo quando o sucesso seria um arquivo.
   */
  resposta?: 'json' | 'crua';
}

/**
 * Renovação do access token.
 *
 * O access dura 15 minutos, então expirar no meio da sessão é rotina, não
 * exceção. A promessa é compartilhada: três requisições que recebem 401 ao
 * mesmo tempo disparariam três rotações de refresh token, e a detecção de
 * reuso do backend interpretaria isso como token roubado e derrubaria a
 * sessão inteira.
 *
 * Estados nomeados, e não um booleano, porque **"não renovou" tem duas causas
 * diferentes e opostas**:
 *
 * - `nao_autenticado` — o refresh foi recusado (401/403): o cookie expirou, foi
 *   revogado ou nunca existiu. A sessão acabou de fato, e derrubá-la é o certo.
 * - `indisponivel` — o servidor falhou (5xx) ou a rede caiu. Isso **não** diz
 *   nada sobre a sessão, e tratá-lo como fim de sessão transformava um erro
 *   momentâneo do backend em logout: quem acabara de entrar era devolvido à tela
 *   de login sem explicação, e o sintoma se lia como "o login não funciona".
 *
 * O quarto estado tem outra natureza — a renovação **funcionou**, e é esse o
 * problema:
 *
 * - `outra_conta` — o refresh devolveu um token válido de **outra pessoa**. Ver
 *   `renovarAccessToken`.
 */
type ResultadoRenovacao = 'renovado' | 'nao_autenticado' | 'indisponivel' | 'outra_conta';

const renovacaoEmCurso: Record<Plano, Promise<ResultadoRenovacao> | null> = {
  TENANT: null,
  OPERADOR: null,
};

const CAMINHO_REFRESH: Record<Plano, string> = {
  TENANT: '/api/v1/auth/refresh',
  OPERADOR: '/api/v1/operacao/auth/refresh',
};

/**
 * Por que a sessão acabou.
 *
 * Duas causas, e elas se explicam de formas diferentes a quem está olhando —
 * ver `shared/lib/saida.ts`, que é quem guarda o motivo para a tela de entrada.
 */
export type MotivoDeSaida = 'sessao_encerrada' | 'outra_conta';

type AoPerderSessao = (plano: Plano, motivo: MotivoDeSaida) => void;
let aoPerderSessao: AoPerderSessao = () => undefined;

export function definirCallbackSessaoPerdida(callback: AoPerderSessao): void {
  aoPerderSessao = callback;
}

/**
 * A conferência de identidade da renovação.
 *
 * Injetada, e não importada: este módulo **não conhece** o store de sessão, e é
 * `sessao.ts` que importa daqui — o caminho contrário fecharia um ciclo. É o
 * mesmo arranjo do `definirCallbackSessaoPerdida`, e por isso quem liga os dois
 * é o mesmo lugar (`sessoes.ts`).
 *
 * `mesma` é a resposta na dúvida: sem sessão em memória, ou com um corpo de
 * resposta que não casa com o contrato, não há como **afirmar** que a conta
 * trocou, e derrubar por dúvida transformaria uma mudança no servidor em logout
 * geral.
 */
export type ConferenciaDeIdentidade = (plano: Plano, identidade: unknown) => 'mesma' | 'outra';

let conferirIdentidade: ConferenciaDeIdentidade = () => 'mesma';

export function definirConferenciaDeIdentidade(conferencia: ConferenciaDeIdentidade): void {
  conferirIdentidade = conferencia;
}

/**
 * A resposta da renovação, nas duas formas.
 *
 * Os dois planos devolvem a mesma coisa sob chaves diferentes: `usuario` no do
 * cliente, `operador` no do console. O tipo do conteúdo é `unknown` de
 * propósito — quem sabe validá-lo é o store de cada plano, com o schema do
 * contrato, e não este módulo.
 */
interface RespostaRenovacao {
  accessToken: string;
  usuario?: unknown;
  operador?: unknown;
}

const CHAVE_IDENTIDADE: Record<Plano, 'usuario' | 'operador'> = {
  TENANT: 'usuario',
  OPERADOR: 'operador',
};

/**
 * Renova o access token — e confere de quem ele é.
 *
 * ## Por que a conferência existe
 *
 * O access token vive **em memória, por aba**. O refresh vive num **cookie**, e
 * cookie é do navegador inteiro: a última conta que entrou em qualquer aba é a
 * dona dele. Quinze minutos depois, a aba esquecida da conta A pede renovação e
 * recebe, com toda a validade do mundo, um token da conta B.
 *
 * Sem a conferência, o resultado era pior do que trocar de conta em silêncio: as
 * consultas passavam a ir para B enquanto o cabeçalho, os papéis e o recorte de
 * empresas continuavam sendo os de A — a tela exibindo o nome de um cliente
 * sobre os dados de outro. E como a requisição original é **repetida** com o
 * token novo, a gravação também mudava de conta: um cadastro que não referencia
 * id nenhum — fornecedor precisa só de tipo, nome e documento — era aceito em B
 * sem recusa nenhuma, porque não há nada ali para o servidor barrar.
 *
 * ## Por que antes de guardar o token
 *
 * A conferência acontece **antes** de `definirAccessToken`. Guardar primeiro e
 * conferir depois deixaria o token alheio em memória por um instante, e qualquer
 * requisição em voo naquele instante sairia com ele.
 *
 * O `id` basta como critério: usuário pertence a uma conta só, então id
 * diferente significa conta diferente. O `tid` do JWT diria o mesmo, mas exigiria
 * decodificar o token no cliente — e o corpo da resposta já traz a identidade
 * pronta e validável pelo contrato.
 */
async function renovarAccessToken(plano: Plano): Promise<ResultadoRenovacao> {
  let resposta: Response;

  try {
    // Sem `Content-Type`: a requisição não tem corpo — o refresh token vem do
    // cookie. Declarar o tipo de um corpo que não existe fazia o Fastify
    // recusar a rota antes do handler, e a renovação nunca acontecia.
    resposta = await fetch(`${BASE}${CAMINHO_REFRESH[plano]}`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Rede caiu, API parada, CORS. Nada disso é recusa de credencial.
    return 'indisponivel';
  }

  if (resposta.status === 401 || resposta.status === 403) return 'nao_autenticado';
  if (!resposta.ok) return 'indisponivel';

  const dados = (await resposta.json()) as RespostaRenovacao;

  if (conferirIdentidade(plano, dados[CHAVE_IDENTIDADE[plano]]) === 'outra') {
    renovacaoBloqueada[plano] = true;
    return 'outra_conta';
  }

  definirAccessToken(dados.accessToken, plano);
  return 'renovado';
}

async function renovarUmaVez(plano: Plano): Promise<ResultadoRenovacao> {
  renovacaoEmCurso[plano] ??= renovarAccessToken(plano).finally(() => {
    renovacaoEmCurso[plano] = null;
  });

  return renovacaoEmCurso[plano];
}

function montarRequisicao(plano: Plano, opcoes: OpcoesRequisicao): RequestInit {
  const { body, idempotencyKey, headers, resposta: _resposta, ...resto } = opcoes;
  const token = tokens[plano];

  return {
    ...resto,
    credentials: 'include',
    headers: {
      // **Só quando há corpo.** `Content-Type: application/json` numa
      // requisição sem corpo é recusado pelo Fastify antes do handler, e era
      // isso que quebrava o `POST /auth/refresh` — que não manda corpo nenhum,
      // porque o token vem do cookie.
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
}

async function interpretar<T>(resposta: Response, modo: 'json' | 'crua' = 'json'): Promise<T> {
  if (resposta.status === 204) return undefined as T;

  // Antes do `json()`, e só no sucesso: ver `OpcoesRequisicao.resposta`.
  if (modo === 'crua' && resposta.ok) return resposta as T;

  const conteudo: unknown = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    const problema: ProblemDetails = (conteudo as ProblemDetails | null) ?? {
      type: 'about:blank',
      title: 'erro_desconhecido',
      status: resposta.status,
      detail: 'Não foi possível concluir a operação.',
    };
    throw new ErroApi(resposta.status, problema);
  }

  return conteudo as T;
}

/** Rotas de sessão não entram no ciclo de renovação — seriam recursivas. */
const CAMINHOS_SEM_RENOVACAO = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/refresh',
  '/api/v1/operacao/auth/login',
  '/api/v1/operacao/auth/refresh',
]);

export async function requisitar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const plano = planoDoCaminho(caminho);
  const resposta = await fetch(`${BASE}${caminho}`, montarRequisicao(plano, opcoes));

  if (resposta.status !== 401 || CAMINHOS_SEM_RENOVACAO.has(caminho)) {
    return interpretar<T>(resposta, opcoes.resposta);
  }

  /*
    Esta aba já descobriu que o cookie pertence a outra conta, e não tenta de
    novo: o 401 sobe para a tela como qualquer outro erro. Ver `renovacaoBloqueada`
    para o que insistir custaria à conta que é dona do cookie.
  */
  if (renovacaoBloqueada[plano]) return interpretar<T>(resposta, opcoes.resposta);

  const renovacao = await renovarUmaVez(plano);

  if (renovacao === 'nao_autenticado' || renovacao === 'outra_conta') {
    // Só o plano da requisição é encerrado. Perder a sessão do console não
    // deve derrubar a do cliente aberta em outra aba, e vice-versa.
    definirAccessToken(null, plano);
    aoPerderSessao(plano, renovacao === 'outra_conta' ? 'outra_conta' : 'sessao_encerrada');
    return interpretar<T>(resposta, opcoes.resposta);
  }

  /*
    Servidor fora do ar ou com erro: a sessão **fica de pé**.

    O erro original sobe para a tela, que o mostra e deixa tentar de novo. A
    alternativa — derrubar a sessão — trocava um erro momentâneo por um logout,
    e o usuário voltava para a tela de entrada achando que o login não tinha
    funcionado. Como a guarda de rota lê o store de sessão, o efeito visível era
    exatamente esse: entrar com sucesso e cair de volta no login.
  */
  if (renovacao === 'indisponivel') return interpretar<T>(resposta, opcoes.resposta);

  const repetida = await fetch(`${BASE}${caminho}`, montarRequisicao(plano, opcoes));
  return interpretar<T>(repetida, opcoes.resposta);
}

/**
 * Monta `?a=1&b=2` ignorando o que estiver vazio, nulo ou indefinido.
 *
 * O tipo do valor é fechado em escalar — e lista de escalares — de propósito:
 * `unknown` aceitaria um objeto, que viraria `[object Object]` na URL, um
 * filtro silenciosamente ignorado pela API e sem erro em lugar nenhum.
 *
 * A **lista vira chave repetida** (`?grupo=CUSTO&grupo=DESPESA`), que é como o
 * Fastify entrega array ao schema da rota. Juntar os valores com vírgula seria
 * a outra opção e está errada: o servidor receberia a string `"CUSTO,DESPESA"`
 * e o `z.enum` a recusaria.
 */
export type EscalarQuery = string | number | boolean | null | undefined;
export type ValorQuery = EscalarQuery | readonly EscalarQuery[];

export function comQuery(caminho: string, params: Record<string, ValorQuery>): string {
  const busca = new URLSearchParams();

  for (const [chave, valor] of Object.entries(params)) {
    for (const item of Array.isArray(valor) ? valor : [valor]) {
      // Lista vazia não escreve nada, e é o certo: `?grupo=` seria um filtro
      // pedindo o grupo de nome vazio, que não devolve linha nenhuma.
      if (item === undefined || item === null || item === '') continue;
      busca.append(chave, String(item));
    }
  }

  const consulta = busca.toString();
  return consulta ? `${caminho}?${consulta}` : caminho;
}

export const api = {
  get: <T>(caminho: string) => requisitar<T>(caminho),
  /**
   * Baixa um arquivo pela API, com o token da sessão.
   *
   * Devolve a `Response` porque quem chama precisa dos **cabeçalhos** tanto
   * quanto do corpo: a exportação do log leva o SHA-256 em `x-conteudo-sha256`,
   * e o nome sugerido no `content-disposition`.
   *
   * Não dá para trocar isto por um `<a href>`: a rota exige `Authorization`, e o
   * link do navegador não manda cabeçalho nenhum.
   */
  arquivo: (caminho: string) => requisitar<Response>(caminho, { resposta: 'crua' }),
  post: <T>(caminho: string, body: unknown, idempotencyKey?: string) =>
    requisitar<T>(caminho, { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) }),
  put: <T>(caminho: string, body: unknown) => requisitar<T>(caminho, { method: 'PUT', body }),
  patch: <T>(caminho: string, body: unknown) => requisitar<T>(caminho, { method: 'PATCH', body }),
  delete: <T>(caminho: string) => requisitar<T>(caminho, { method: 'DELETE' }),
};
